import { fetchIslamicEventsForYear } from './aladhanApi';

export interface IslamicEvent {
  id: string;
  name: string;
  nameArabic: string;
  date: Date;
  type: 'eid' | 'ramadan' | 'hajj' | 'special';
  description: string;
  hijriDate?: string;
}

let cachedEvents: IslamicEvent[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 24 * 60 * 60 * 1000;

export const getIslamicEvents = async (): Promise<IslamicEvent[]> => {
  const now = Date.now();

  if (cachedEvents && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    return cachedEvents;
  }

  try {
    const events = await fetchIslamicEventsForYear();

    if (events.length > 0) {
      cachedEvents = events;
      cacheTimestamp = now;
      return events;
    }

    return getFallbackEvents();
  } catch (error) {
    console.error('Error fetching Islamic events from API:', error);
    return getFallbackEvents();
  }
};

function getFallbackEvents(): IslamicEvent[] {
  return [
    {
      id: 'lailat-al-baraa',
      name: "Lailat al-Bara'ah",
      nameArabic: "ليلة البراءة",
      date: new Date(2026, 1, 3),
      type: 'special',
      description: "15th of Sha'ban - Night of Forgiveness",
      hijriDate: "15th Shaban 1447"
    },
    {
      id: 'ramadan-start',
      name: "Ramadan Begins",
      nameArabic: "بداية رمضان",
      date: new Date(2026, 1, 18),
      type: 'ramadan',
      description: "The blessed month of fasting begins",
      hijriDate: "1st Ramadan 1447"
    },
    {
      id: 'ramadan-middle',
      name: "Middle of Ramadan",
      nameArabic: "منتصف رمضان",
      date: new Date(2026, 2, 4),
      type: 'ramadan',
      description: "15th night of Ramadan",
      hijriDate: "15th Ramadan 1447"
    },
    {
      id: 'ramadan-last10',
      name: "Last 10 Nights Begin",
      nameArabic: "بداية العشر الأواخر",
      date: new Date(2026, 2, 9),
      type: 'ramadan',
      description: "Most blessed nights of Ramadan",
      hijriDate: "21st Ramadan 1447"
    },
    {
      id: 'laylat-qadr',
      name: "Laylat al-Qadr",
      nameArabic: "ليلة القدر",
      date: new Date(2026, 2, 15),
      type: 'special',
      description: "Night of Power (27th of Ramadan)",
      hijriDate: "27th Ramadan 1447"
    },
    {
      id: 'eid-fitr',
      name: "Eid al-Fitr",
      nameArabic: "عيد الفطر",
      date: new Date(2026, 2, 20),
      type: 'eid',
      description: "Festival marking the end of Ramadan",
      hijriDate: "1st Shawwal 1447"
    },
    {
      id: 'hajj-begins',
      name: "Hajj Begins",
      nameArabic: "بداية الحج",
      date: new Date(2026, 4, 22),
      type: 'hajj',
      description: "The pilgrimage to Mecca begins",
      hijriDate: "8th Dhul Hijjah 1447"
    },
    {
      id: 'arafah',
      name: "Day of Arafah",
      nameArabic: "يوم عرفة",
      date: new Date(2026, 4, 26),
      type: 'hajj',
      description: "9th of Dhul Hijjah - The most important day of Hajj",
      hijriDate: "9th Dhul Hijjah 1447"
    },
    {
      id: 'eid-adha',
      name: "Eid al-Adha",
      nameArabic: "عيد الأضحى",
      date: new Date(2026, 4, 27),
      type: 'eid',
      description: "Festival of Sacrifice",
      hijriDate: "10th Dhul Hijjah 1447"
    },
    {
      id: 'islamic-new-year',
      name: "Islamic New Year 1448",
      nameArabic: "رأس السنة الهجرية ١٤٤٨",
      date: new Date(2026, 5, 17),
      type: 'special',
      description: "First of Muharram",
      hijriDate: "1st Muharram 1448"
    },
    {
      id: 'ashura',
      name: "Ashura",
      nameArabic: "عاشوراء",
      date: new Date(2026, 5, 26),
      type: 'special',
      description: "10th of Muharram - Day of remembrance",
      hijriDate: "10th Muharram 1448"
    },
    {
      id: 'mawlid',
      name: "Eid Milad un Nabi",
      nameArabic: "عيد المولد النبوي",
      date: new Date(2026, 7, 25),
      type: 'special',
      description: "Birthday of Prophet Muhammad (PBUH)",
      hijriDate: "12th Rabi Al Awwal 1448"
    }
  ];
}

export const getUpcomingEvents = async (daysAhead: number = 30): Promise<IslamicEvent[]> => {
  const now = new Date();
  const futureDate = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);

  const events = await getIslamicEvents();
  return events.filter(event =>
    event.date >= now && event.date <= futureDate
  );
};

export const getCurrentEvent = async (): Promise<IslamicEvent | null> => {
  const now = new Date();
  const events = await getIslamicEvents();

  for (const event of events) {
    const eventDate = new Date(event.date);
    const timeDiff = Math.abs(now.getTime() - eventDate.getTime());
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    if (daysDiff <= 3) {
      return event;
    }
  }

  return null;
};

export interface RamadanInfo {
  isRamadan: boolean;
  dayOfRamadan: number;
  daysRemaining: number;
  startDate: Date;
  endDate: Date;
}

export const getRamadanInfo = async (): Promise<RamadanInfo | null> => {
  const now = new Date();
  const events = await getIslamicEvents();

  const ramadanStart = events.find(e => e.id === 'ramadan-start');
  const eidAlFitr = events.find(e => e.id === 'eid-fitr');

  if (!ramadanStart || !eidAlFitr) return null;

  const startDate = new Date(ramadanStart.date);
  const endDate = new Date(eidAlFitr.date);

  // Check if we're currently in Ramadan
  if (now >= startDate && now < endDate) {
    const daysPassed = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const dayOfRamadan = daysPassed + 1;
    const daysRemaining = 30 - dayOfRamadan;

    return {
      isRamadan: true,
      dayOfRamadan,
      daysRemaining,
      startDate,
      endDate
    };
  }

  return {
    isRamadan: false,
    dayOfRamadan: 0,
    daysRemaining: 0,
    startDate,
    endDate
  };
};
